import { z } from 'zod';

import { calendarDateSchema } from './employee';
import { emailSchema, usernameSchema } from './user';
import { PEOPLE_SORTS, activityFilterSchema, listQuerySchema, sortParam } from './pagination';

/*
 * Clients, their properties, notes and preferences.
 * reference/data-model.md §3, ROADMAP.md §3.5 and §3.6.
 */

const trimmed = (max: number) =>
  z
    .string()
    .trim()
    .min(1, 'Required')
    .max(max, `Must be ${String(max)} characters or fewer`);

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Must be ${String(max)} characters or fewer`)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional();

const optionalEmail = z
  .union([emailSchema, z.literal('')])
  .transform((value) => (value === '' ? null : value))
  .nullable()
  .optional();

// ---------------------------------------------------------------------------
// client
// ---------------------------------------------------------------------------

const clientFields = z.object({
  firstName: trimmed(128),
  lastName: trimmed(128),

  /**
   * A company rather than a person.
   *
   * The prototype's "Property Manager" is a business, and rendering a business
   * as "first last" is how a letter goes out addressed to nobody. When this is
   * set it leads on every screen, with the person shown as the contact.
   */
  businessName: optional(255),

  email: optionalEmail,
  phone: optional(32),

  /** Gates calendar sync — business-rules.md §6. Not a display preference. */
  isHighPriority: z.boolean(),

  /**
   * 1–5, internal. Open question 8 is answered: this is a note the office
   * keeps, never something the client sees.
   */
  satisfactionRating: z
    .number()
    .int()
    .min(1, 'A rating runs from 1 to 5')
    .max(5, 'A rating runs from 1 to 5')
    .nullable()
    .optional(),

  /** Half the P10 fix — the prototype renders "Customer since —" forever. */
  customerSince: calendarDateSchema.nullable().optional(),
});

/** A client has to be reachable. Same family of rule as the one on `user`. */
const contactable = {
  check: (value: { email?: string | null; phone?: string | null }): boolean =>
    Boolean(value.email ?? value.phone),
  message: 'Give an email address or a phone number — otherwise nobody can reach them',
  path: ['email'] as const,
};

export const createClientSchema = clientFields.refine(contactable.check, {
  message: contactable.message,
  path: [...contactable.path],
});

export type CreateClientInput = z.infer<typeof createClientSchema>;

export const updateClientSchema = clientFields
  .extend({ isActive: z.boolean().optional() })
  .refine(contactable.check, { message: contactable.message, path: [...contactable.path] });

export type UpdateClientInput = z.infer<typeof updateClientSchema>;

export const clientListQuerySchema = listQuerySchema.extend({
  sort: sortParam(PEOPLE_SORTS, 'name'),

  activity: activityFilterSchema,
  /** "Only the ones calendar sync is available to." */
  highPriorityOnly: z
    .union([z.literal('true'), z.literal('false')])
    .transform((value) => value === 'true')
    .optional(),
});

// ---------------------------------------------------------------------------
// client_property
// ---------------------------------------------------------------------------

export const clientPropertySchema = z.object({
  nickname: optional(128),

  address: trimmed(255),
  city: trimmed(128),
  state: trimmed(64),
  zip: trimmed(16),

  bedrooms: z.number().int().min(0).max(100).nullable().optional(),
  /**
   * Half-steps allowed, because 2.5 baths is a real number of bathrooms and the
   * column is `decimal(3,1)`. Anything finer would not survive the round trip.
   */
  bathrooms: z
    .number()
    .min(0)
    .max(99)
    .refine((value) => Number.isInteger(value * 2), 'Bathrooms go in halves — 2, 2.5, 3')
    .nullable()
    .optional(),
  squareFeet: z.number().int().min(0).max(1_000_000).nullable().optional(),

  categoryId: z.uuid().nullable().optional(),
  specialInstructions: optional(4000),

  /**
   * **Sensitive.** Lockbox codes, gate codes, alarm codes, where the dog is.
   *
   * Admin always; an employee only for a property on a job they are assigned to
   * (business-rules.md §2). Phase 4 is where the second half becomes
   * enforceable. Nothing outside `/api/admin/*` returns this today.
   */
  accessNotes: optional(4000),

  /** Exactly one primary per client — the service does that in a transaction. */
  isPrimary: z.boolean().optional(),
});

export type ClientPropertyInput = z.infer<typeof clientPropertySchema>;

// ---------------------------------------------------------------------------
// client_note and client_preference
// ---------------------------------------------------------------------------

/**
 * What a note is about — migration 030, and the prototype's own six.
 *
 * **Never rendered raw** (CLAUDE.md §4, defect P5). `follow_up` is the one that
 * would give it away.
 */
export const CLIENT_NOTE_TYPE_VALUES = [
  'general',
  'service',
  'complaint',
  'feedback',
  'follow_up',
  'billing',
] as const;
export type ClientNoteTypeValue = (typeof CLIENT_NOTE_TYPE_VALUES)[number];

export const CLIENT_NOTE_TYPE_LABELS: Record<ClientNoteTypeValue, string> = {
  general: 'General',
  service: 'Service',
  complaint: 'Complaint',
  feedback: 'Feedback',
  follow_up: 'Follow-up',
  billing: 'Billing',
};

export const clientNoteSchema = z.object({
  body: z.string().trim().min(1, 'Say something').max(4000),
  /**
   * Defaulted rather than required. A note somebody could not be bothered to
   * classify is still a note worth having, and a required dropdown would mean
   * every note filed as whatever sits at the top of the list.
   */
  noteType: z.enum(CLIENT_NOTE_TYPE_VALUES).default('general'),
  isPinned: z.boolean().default(false),
  isPrivate: z.boolean().default(false),
});

export type ClientNoteInput = z.input<typeof clientNoteSchema>;

/** Pinning is the one thing about a note that may change after it is written. */
export const clientNotePinSchema = z.object({ isPinned: z.boolean() });

export const CONTACT_METHOD_VALUES = ['email', 'phone', 'text'] as const;
export type ContactMethodValue = (typeof CONTACT_METHOD_VALUES)[number];

export const CONTACT_METHOD_LABELS: Record<ContactMethodValue, string> = {
  email: 'Email',
  phone: 'Phone call',
  text: 'Text message',
};

export const clientPreferenceSchema = z.object({
  /** 0–6, Sunday first. */
  preferredDays: z.array(z.number().int().min(0).max(6)).max(7).nullable().optional(),
  /** `HH:MM`. A wall-clock time, never an instant. */
  preferredTime: z
    .union([z.string().regex(/^\d{2}:\d{2}$/, 'Use a time like 09:00'), z.literal('')])
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .optional(),
  /**
   * Null is "nobody has asked", which the screen says in those words rather
   * than defaulting somebody to email and being quietly wrong about it.
   */
  preferredContactMethod: z
    .union([z.enum(CONTACT_METHOD_VALUES), z.literal('')])
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .optional(),
  specialRequirements: optional(4000),
  petNotes: optional(2000),
  /** A safety fact, not a preference. Reaches the cleaner — see the migration. */
  allergyNotes: optional(2000),
});

export type ClientPreferenceInput = z.infer<typeof clientPreferenceSchema>;

// ---------------------------------------------------------------------------
// The portal login — ROADMAP.md §5.1
// ---------------------------------------------------------------------------

/**
 * Opening the client portal to a customer who already exists in the office's
 * records.
 *
 * This is the thing that was missing until phase 5: `client.user_id` is
 * nullable, employees got their login at the moment they were hired (3.4), and
 * nothing at all created one for a client. Without this action the portal opens
 * onto nothing, because there is no row behind the account.
 *
 * Same identifier rules as every other account, stated once in `createUserSchema`
 * and enforced there — an email or a username, and a username needs a recovery
 * address or the person can never get back in (P9).
 */
export const clientPortalLoginSchema = z.object({
  email: emailSchema.optional(),
  username: usernameSchema.optional(),
  recoveryEmail: emailSchema.optional(),
});

export type ClientPortalLoginInput = z.infer<typeof clientPortalLoginSchema>;

/** What the login screen shows about a client's account, if they have one. */
export interface ClientPortalLoginView {
  userId: string;
  email: string | null;
  username: string | null;
  recoveryEmail: string | null;
  isActive: boolean;
  /** Null until they have signed in once — "Never", not a dash. */
  lastLoginAt: string | null;
}

/** The one-time password, shown once on a screen that says so. */
export interface CreatedClientPortalLogin {
  login: ClientPortalLoginView;
  temporaryPassword: string;
}

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

export interface ClientPropertyView {
  id: string;
  clientId: string;
  nickname: string | null;
  isPrimary: boolean;
  address: string;
  city: string;
  state: string;
  zip: string;
  /** "728 W North Cedar Blvd, Cedar City, UT 84720" — built once, on the server. */
  fullAddress: string;
  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;
  categoryId: string | null;
  /** The display value. Never a slug — P5. */
  categoryName: string | null;
  specialInstructions: string | null;
  /** Admin responses only. See the schema above. */
  accessNotes: string | null;
  isActive: boolean;
}

export interface ClientNoteView {
  id: string;
  body: string;
  noteType: ClientNoteTypeValue;
  /** The display value. Never the stored one — P5. */
  noteTypeLabel: string;
  isPinned: boolean;
  isPrivate: boolean;
  authorName: string | null;
  createdAt: string;
}

export interface ClientPreferenceView {
  preferredDays: number[] | null;
  preferredTime: string | null;
  preferredContactMethod: ContactMethodValue | null;
  /** Null when unset, so the screen can say "nobody has asked" rather than guess. */
  preferredContactMethodLabel: string | null;
  specialRequirements: string | null;
  petNotes: string | null;
  allergyNotes: string | null;
}

export interface ClientView {
  id: string;
  userId: string | null;
  firstName: string;
  lastName: string;
  businessName: string | null;
  /** The business name when there is one, otherwise the person. Built server-side. */
  displayName: string;
  /** Always the person, so a business row can still show who to ask for. */
  contactName: string;
  email: string | null;
  phone: string | null;
  isHighPriority: boolean;
  satisfactionRating: number | null;
  customerSince: string | null;
  isActive: boolean;
  /** How many active properties. Zero is `0` — P1. */
  propertyCount: number;
  /** The primary property's address, or null when they have no property yet. */
  primaryAddress: string | null;
}

/**
 * What this customer has been worth — **two numbers, because there are two
 * honest answers.** Owner decision, 2026-08-06: collected is the headline,
 * because it is the figure that reconciles against the bank; billed sits beside
 * it, because the difference between them is what is still owed.
 *
 * Both are integer cents over the whole relationship, not a window. Void
 * invoices count in neither — a voided invoice was un-billed — and a refunded
 * payment is not collected, on the same reasoning the reports engine uses.
 */
export interface ClientLifetimeValue {
  billedCents: number;
  collectedCents: number;
}

/**
 * The last time anybody spoke to this customer, and what it was.
 *
 * **Derived on read, never stored.** Same call as `CustomerTimeline` in ROADMAP
 * §9.7: a column would have to be updated by every write path that counts as
 * contact — a reply, a sent invoice, a logged phone call — and the one that
 * eventually forgot would make the field quietly wrong, which is worse than not
 * having it. It is one indexed read over messages the thread already orders.
 *
 * Null is "never", which is a real answer for a client the office has only ever
 * cleaned for. It is rendered as that word, not as a dash (P1, P10).
 */
export interface ClientLastContact {
  at: string;
  /** "They wrote", "We replied", "Phone call" — already in words. */
  description: string;
}

export interface ClientDetail extends ClientView {
  properties: ClientPropertyView[];
  notes: ClientNoteView[];
  preferences: ClientPreferenceView;
  lifetime: ClientLifetimeValue;
  /** Null means nobody has ever been in touch. See the interface. */
  lastContact: ClientLastContact | null;
  /** Null when this client cannot sign in to the portal yet. */
  portalLogin: ClientPortalLoginView | null;
}

/** Sunday first, matching `preferred_days` and `overtime_week_starts_on`. */
export const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
